import {
  CSSProperties,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Textarea, type TextareaAPI, type TextareaProps } from '@ets/design-system';
import cn from 'classnames';

import { useDispatch } from '_common/hooks';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';

import { IntlErrorBoundary, LineEllipsisv2 } from '_common/components';

import styles from './styles.module.scss';

type RichTextLabelProps = Pick<TextareaProps, 'initialContent' | 'testId' | 'unstyled'> & {
  /**
   * If true, the text will be collapsed and a read more/less button will be displayed
   */
  collapse?: boolean | 'static';
  /**
   * The maximum number of lines to display when collapsed
   * @default 3
   */
  maxLines?: number;
  /**
   * If true, it will occupy the full width of the parent container
   */
  fullWidth?: boolean;
  style?: CSSProperties;
  className?: string;
};

export const RichTextLabel = forwardRef<TextareaAPI, RichTextLabelProps>(
  ({ collapse, maxLines, fullWidth, initialContent, style, className, ...props }, outerRef) => {
    const dispatch = useDispatch();

    const innerRef = useRef<TextareaAPI>(null);
    useImperativeHandle(outerRef, () => innerRef.current!);

    //InitialContent might be delayed, so we need to update the value when it arrives
    useEffect(() => {
      if (initialContent) {
        innerRef.current?.setValue(initialContent);
      }
    }, [initialContent]);

    const [clamped, setClamped] = useState(true);

    const rootElement = innerRef.current?.getRootElement();
    const paragraph = rootElement?.querySelector('.ProseMirror p') as HTMLElement | null;
    const elIsClamped = paragraph ? paragraph.scrollHeight > paragraph.clientHeight : undefined;

    const options = useMemo<TextareaProps['options']>(() => {
      return {
        link: {
          onClick: (href) => {
            dispatch(
              openAndUpdateModal({
                modal: 'OpenHyperlinkModal',
                data: {
                  url: href,
                },
              }),
            );
          },
        },
      };
    }, []);

    return (
      <IntlErrorBoundary>
        <Textarea
          className={cn(styles.root, className, {
            [styles.clamped]: collapse && clamped,
            [styles.fullWidth]: fullWidth,
          })}
          style={{ WebkitLineClamp: maxLines, ...style }}
          name="label"
          type="json"
          size="m"
          readOnly
          {...props}
          initialContent={initialContent}
          options={options}
          ref={innerRef}
        />
        {collapse && collapse !== 'static' ? (
          <div className="px-4">
            <LineEllipsisv2 initialClamped={!!elIsClamped} onClampChange={setClamped} />
          </div>
        ) : null}
      </IntlErrorBoundary>
    );
  },
);
